import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Save, Trash2, X } from 'lucide-react';

import { observer } from 'mobx-react';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';
import { ImageUpload } from '@components/image-upload';
import LocalizedTextArea from '@components/text-area/localized-text-area';
import { useWrapRequest } from '@components/hooks/use-wrap-request';
import { FormSection } from '@components/form-section';
import { PageHeader } from '@components/page-header';
import { Spinner } from '@components/spinner';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription, AlertTitle } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import {
    CdnObjectDto,
    CommunityDetailsSupportDto,
    CommunityUpdateRequestSupportDto,
    CommunityVisibility,
    Consent,
    ConsentUpdateDto,
    I18NText,
    ImageType,
    LanguageCode,
    LocaleCode
} from '@core/api/ChallengeClient';
import { links } from '@core/router';
import { getCdnImage } from '@components/utils/cdn-image';
import { ColorPicker } from '@components/color-picker';

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

const SwitchField = ({
    label,
    checked,
    disabled,
    onChange
}: {
    label: string;
    checked: boolean;
    disabled?: boolean;
    onChange(checked: boolean): void;
}) => {
    const id = React.useId();
    return (
        <div className="flex items-center gap-2">
            <Switch
                id={id}
                checked={checked}
                disabled={disabled}
                onCheckedChange={onChange}
            />
            <Label htmlFor={id}>{label}</Label>
        </div>
    );
};

const EditCommunityForm = observer(() => {
    const { communityId } = useParams();
    const navigate = useNavigate();
    const { challengeClient } = injectTSDI(Api);
    const ensureMemberAccounts = useWrapRequest(
        async (id: number) => {
            await challengeClient.support.communitySupportControllerTriggerWalletAccountSynchronization(
                id
            );
        },
        {
            successMessage: () => 'Member wallet accounts ensured'
        }
    );

    const [community, setCommunity] =
        useState<CommunityDetailsSupportDto | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [imageUploadLoading, setImageUploadLoading] = useState(false);
    const [imageUploadError, setImageUploadError] = useState<
        string | undefined
    >();
    const [_, setImagePreviewUrl] = useState<string | undefined>();

    const [backgroundImageUploadLoading, setBackgroundImageUploadLoading] =
        useState(false);
    const [backgroundImageUploadError, setBackgroundImageUploadError] =
        useState<string | undefined>();
    const [__, setBackgroundImagePreviewUrl] = useState<string | undefined>();

    const [consents, setConsents] = useState<Consent[]>([]);
    const [consentErrors, setConsentErrors] = useState<(string | null)[]>([]);

    const [formData, setFormData] = useState({
        name: '',
        description: { en: '' } as I18NText,
        memberLimit: '',
        approved: false,
        photoBucketName: '',
        photoObjectKey: '',
        backgroundImage: null as CdnObjectDto | null,
        visibility: '',
        color: '',
        pushNotificationEnabled: false,
        welcomeMessageEnabled: false,
        tenantMemberAutoJoinEnabled: false,
        supportedLanguages: '',
        currencyCode: '',
        tenantId: '',
        locale: ''
    });

    useEffect(() => {
        const fetchCommunity = async () => {
            if (!communityId) {
                setError('Community ID is required');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const communityIdNumber = parseInt(communityId, 10);

                const response =
                    await challengeClient.support.communitySupportControllerV2Get(
                        communityIdNumber
                    );
                const communityData = response.data;
                setCommunity(communityData);

                const description = communityData.description as
                    | I18NText
                    | undefined;
                setFormData({
                    name: communityData.name || '',
                    description: description || { en: '' },
                    memberLimit: '',
                    approved: communityData.approved || false,
                    photoBucketName: communityData.photo?.bucketName || '',
                    photoObjectKey: communityData.photo?.objectKey || '',
                    backgroundImage: communityData.backgroundImage || null,
                    color: communityData.color || '#4bbbbd',
                    visibility: communityData.visibility || '',
                    pushNotificationEnabled:
                        communityData.pushNotificationEnabled || false,
                    welcomeMessageEnabled:
                        communityData.welcomeMessageEnabled || false,
                    tenantMemberAutoJoinEnabled:
                        communityData.tenantMemberAutoJoinEnabled || false,
                    supportedLanguages: communityData.supportedLanguages
                        .map((it) => it.toString())
                        .join(','),
                    currencyCode: communityData.currencyCode || '',
                    tenantId: communityData.tenantId || '',
                    locale: communityData.locale || ''
                });

                setConsents(communityData.joinConsents || []);

                if (
                    communityData.photo?.bucketName &&
                    communityData.photo?.objectKey
                ) {
                    setImagePreviewUrl(undefined);
                }
            } catch (err: any) {
                console.error('Error fetching community:', err);
                setError(
                    err.response?.data?.message ||
                        err.message ||
                        'Failed to fetch community details'
                );
            } finally {
                setLoading(false);
            }
        };

        fetchCommunity();
    }, [communityId, challengeClient]);

    const handleImageUpload = async (file: File) => {
        setImageUploadLoading(true);
        setImageUploadError(undefined);

        try {
            const result =
                await challengeClient.support.imageSupportControllerUpload(
                    { type: ImageType.COMMUNITY },
                    { file }
                );

            setFormData((prev) => ({
                ...prev,
                photoBucketName: result.data.bucketName,
                photoObjectKey: result.data.objectKey
            }));

            const previewUrl = URL.createObjectURL(file);
            setImagePreviewUrl(previewUrl);
        } catch (error) {
            console.error('Image upload failed:', error);
            setImageUploadError('Failed to upload image. Please try again.');
        } finally {
            setImageUploadLoading(false);
        }
    };

    const handleBackgroundImageUpload = async (file: File) => {
        setBackgroundImageUploadLoading(true);
        setBackgroundImageUploadError(undefined);

        try {
            const result =
                await challengeClient.support.imageSupportControllerUpload(
                    { type: ImageType.COMMUNITY },
                    { file }
                );

            setFormData((prev) => ({
                ...prev,
                backgroundImage: {
                    bucketName: result.data.bucketName,
                    objectKey: result.data.objectKey
                }
            }));

            const previewUrl = URL.createObjectURL(file);
            setBackgroundImagePreviewUrl(previewUrl);
        } catch (error) {
            console.error('Background image upload failed:', error);
            setBackgroundImageUploadError(
                'Failed to upload background image. Please try again.'
            );
        } finally {
            setBackgroundImageUploadLoading(false);
        }
    };

    const handleFieldChange =
        (field: string) => (value: string | number | boolean) => {
            setFormData((prev) => ({
                ...prev,
                [field]: value
            }));
        };

    const handleDescriptionChange = (value: I18NText) => {
        setFormData((prev) => ({
            ...prev,
            description: value
        }));
    };

    const handleColorChange = (color: string) => {
        setFormData((prev) => ({
            ...prev,
            color
        }));
    };

    const handleClearBackgroundImage = () => {
        setFormData((prev) => ({
            ...prev,
            backgroundImage: null
        }));
        setBackgroundImagePreviewUrl(undefined);
    };

    const addConsent = () => {
        setConsents((prev) => [
            ...prev,
            {
                text: {},
                needsCheckbox: true,
                required: true,
                showAfterAccept: true
            }
        ]);
    };

    const removeConsent = (index: number) => {
        setConsents((prev) => prev.filter((_, i) => i !== index));
        setConsentErrors((prev) => prev.filter((_, i) => i !== index));
    };

    const updateConsentText = (index: number, text: I18NText) => {
        setConsents((prev) =>
            prev.map((c, i) => (i === index ? { ...c, text } : c))
        );
    };

    const updateConsentToggle = (
        index: number,
        field: 'needsCheckbox' | 'required' | 'showAfterAccept',
        value: boolean
    ) => {
        setConsents((prev) =>
            prev.map((c, i) => {
                if (i !== index) return c;
                const updated = { ...c, [field]: value };
                if (field === 'needsCheckbox') {
                    updated.required = value;
                }
                if (field === 'required' && !value) {
                    updated.showAfterAccept = false;
                }
                return updated;
            })
        );
    };

    const validateConsents = (): boolean => {
        if (formData.tenantMemberAutoJoinEnabled) return true;
        const errors = consents.map((c) => {
            const hasText = Object.values(c.text).some((v) => v && v.trim());
            return hasText ? null : 'At least one language is required';
        });
        setConsentErrors(errors);
        return !errors.some((e) => e !== null);
    };

    const handleSave = async () => {
        if (!community) {
            return;
        }

        if (!validateConsents()) {
            return;
        }

        try {
            setSaving(true);
            setError(null);

            const description: I18NText = {};
            Object.entries(formData.description).forEach(([key, value]) => {
                if (value && value.trim()) {
                    description[key as keyof I18NText] = value.trim();
                }
            });

            const photo: CdnObjectDto = {
                bucketName: formData.photoBucketName.trim() || 'default-bucket',
                objectKey: formData.photoObjectKey.trim() || 'default-key'
            };

            const updateData: CommunityUpdateRequestSupportDto = {
                name: formData.name.trim(),
                description:
                    Object.keys(description).length > 0
                        ? description
                        : undefined,
                photo: photo,
                memberLimit: formData.memberLimit
                    ? parseInt(formData.memberLimit, 10)
                    : undefined,
                approved: formData.approved,
                visibility: formData.visibility as CommunityVisibility,
                backgroundImage: formData.backgroundImage || undefined,
                color: formData.color || undefined,
                pushNotificationEnabled: formData.pushNotificationEnabled,
                welcomeMessageEnabled: formData.welcomeMessageEnabled,
                tenantMemberAutoJoinEnabled:
                    formData.tenantMemberAutoJoinEnabled,
                supportedLanguages:
                    formData.supportedLanguages.trim().length > 0
                        ? formData.supportedLanguages
                              .split(',')
                              .map((it) => it as LanguageCode)
                        : [],
                currencyCode: formData.currencyCode.trim() || undefined,
                tenantId: formData.tenantId.trim() || undefined,
                locale: formData.locale as LocaleCode
            };

            await challengeClient.support.communitySupportControllerUpdateCommunity(
                community.id!,
                updateData
            );

            const consentData: ConsentUpdateDto = {
                joinConsents: formData.tenantMemberAutoJoinEnabled
                    ? []
                    : consents
            };
            await challengeClient.support.communitySupportControllerUpdateJoinConsents(
                community.id!,
                consentData
            );

            navigate(links.communityDetails(community.id!.toString()));
        } catch (err: any) {
            console.error('Error updating community:', err);
            setError(
                err.response?.data?.message ||
                    err.message ||
                    'Failed to update community'
            );
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        if (community) {
            navigate(links.communityDetails(community.id!.toString()));
        } else {
            navigate(links.communities());
        }
    };

    const isFormValid = formData.name.trim().length > 0;
    const persistedCurrencyCode = community?.currencyCode?.trim() || '';
    const currentCurrencyCode = formData.currencyCode.trim();
    const hasPersistedCurrencyCode = persistedCurrencyCode.length > 0;
    const hasUnsavedCurrencyChange =
        community !== null && currentCurrencyCode !== persistedCurrencyCode;

    const handleEnsureMemberAccounts = async () => {
        if (
            !community?.id ||
            !hasPersistedCurrencyCode ||
            hasUnsavedCurrencyChange
        ) {
            return;
        }

        await ensureMemberAccounts.request([community.id]);
    };

    if (loading) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <Spinner />
            </div>
        );
    }

    if (error && !community) {
        return (
            <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <>
            <PageHeader
                title={`Edit Community: ${community?.name || 'Loading...'}`}
            />

            {error && (
                <Alert variant="destructive" className="mb-6">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}

            <form
                className="flex flex-col gap-6 pb-24"
                onSubmit={(e) => e.preventDefault()}
            >
                <FormSection title="Basic Information">
                    <div className="grid gap-4 md:grid-cols-2">
                        <TextField
                            label="Community Name"
                            type="text"
                            value={formData.name}
                            onChange={handleFieldChange('name')}
                            required
                            error={
                                !formData.name.trim()
                                    ? 'Community name is required'
                                    : undefined
                            }
                        />
                        <TextField
                            label="Tenant ID"
                            type="text"
                            value={formData.tenantId}
                            onChange={handleFieldChange('tenantId')}
                        />
                        <div className="flex flex-col gap-1.5">
                            <TextField
                                label="Member Limit"
                                type="number"
                                value={formData.memberLimit}
                                onChange={handleFieldChange('memberLimit')}
                            />
                            <Hint>Leave empty for no limit</Hint>
                        </div>
                        <TextField
                            label="Visibility"
                            type="text"
                            value={formData.visibility}
                            onChange={handleFieldChange('visibility')}
                            required
                            error={
                                !formData.visibility.trim()
                                    ? 'Visibility is required'
                                    : undefined
                            }
                        />
                        <TextField
                            label="Supported languages"
                            type="text"
                            value={formData.supportedLanguages}
                            onChange={handleFieldChange('supportedLanguages')}
                        />
                        <div className="flex flex-col gap-1.5">
                            <TextField
                                label="Locale"
                                type="text"
                                value={formData.locale}
                                onChange={handleFieldChange('locale')}
                                required
                                error={
                                    !formData.locale.trim()
                                        ? 'Locale is required'
                                        : undefined
                                }
                            />
                            {formData.locale.trim() && (
                                <Hint>
                                    e.g., en_GB, de_DE, pl_PL, nb_NO, da_DK
                                </Hint>
                            )}
                        </div>
                        <div className="flex flex-col gap-1.5 md:col-span-2">
                            <div className="flex items-end gap-4">
                                <TextField
                                    label="Currency code"
                                    type="text"
                                    value={formData.currencyCode}
                                    onChange={handleFieldChange('currencyCode')}
                                />
                                <Button
                                    variant="outline"
                                    className="min-w-[220px]"
                                    onClick={() =>
                                        void handleEnsureMemberAccounts()
                                    }
                                    loading={ensureMemberAccounts.loading}
                                    disabled={
                                        !hasPersistedCurrencyCode ||
                                        hasUnsavedCurrencyChange
                                    }
                                >
                                    Ensure member accounts
                                </Button>
                            </div>
                            {hasUnsavedCurrencyChange && (
                                <p className="text-xs text-warning">
                                    Save community changes before syncing
                                    accounts for the new currency.
                                </p>
                            )}
                            {!hasPersistedCurrencyCode && (
                                <Hint>
                                    Set and save a community currency code
                                    before ensuring member accounts.
                                </Hint>
                            )}
                        </div>
                        <div className="flex flex-col gap-3 md:col-span-2">
                            <SwitchField
                                label="Approved"
                                checked={formData.approved}
                                onChange={handleFieldChange('approved')}
                            />
                            <SwitchField
                                label="Push notifications enabled"
                                checked={formData.pushNotificationEnabled}
                                onChange={handleFieldChange(
                                    'pushNotificationEnabled'
                                )}
                            />
                            <SwitchField
                                label="Welcome message enabled"
                                checked={formData.welcomeMessageEnabled}
                                onChange={handleFieldChange(
                                    'welcomeMessageEnabled'
                                )}
                            />
                            <SwitchField
                                label="Tenant auto-join enabled"
                                checked={formData.tenantMemberAutoJoinEnabled}
                                onChange={handleFieldChange(
                                    'tenantMemberAutoJoinEnabled'
                                )}
                            />
                        </div>
                    </div>
                </FormSection>

                <FormSection title="Photo Settings">
                    <div className="flex flex-col gap-4">
                        <ImageUpload
                            onUpload={handleImageUpload}
                            loading={imageUploadLoading}
                            error={imageUploadError}
                            previewUrl={
                                formData.photoBucketName &&
                                formData.photoObjectKey
                                    ? getCdnImage(
                                          formData.photoBucketName,
                                          formData.photoObjectKey
                                      )
                                    : undefined
                            }
                            label="Community Photo"
                            helperText="Drag and drop a community photo here, or click to select"
                        />
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="flex flex-col gap-1.5">
                                <TextField
                                    label="Photo Bucket Name"
                                    type="text"
                                    value={formData.photoBucketName}
                                    onChange={handleFieldChange(
                                        'photoBucketName'
                                    )}
                                    disabled
                                />
                                <Hint>Auto-filled when image is uploaded</Hint>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <TextField
                                    label="Photo Object Key"
                                    type="text"
                                    value={formData.photoObjectKey}
                                    onChange={handleFieldChange(
                                        'photoObjectKey'
                                    )}
                                    disabled
                                />
                                <Hint>Auto-filled when image is uploaded</Hint>
                            </div>
                        </div>
                    </div>
                </FormSection>

                <FormSection
                    title="Background Image Settings"
                    description="Community Background Image"
                    actions={
                        <Button
                            variant="destructive"
                            size="sm"
                            onClick={handleClearBackgroundImage}
                            disabled={!formData.backgroundImage}
                        >
                            <X />
                            Clear
                        </Button>
                    }
                >
                    <div className="flex flex-col gap-4">
                        <ImageUpload
                            onUpload={handleBackgroundImageUpload}
                            loading={backgroundImageUploadLoading}
                            error={backgroundImageUploadError}
                            previewUrl={
                                formData.backgroundImage?.bucketName &&
                                formData.backgroundImage?.objectKey
                                    ? getCdnImage(
                                          formData.backgroundImage.bucketName,
                                          formData.backgroundImage.objectKey
                                      )
                                    : undefined
                            }
                            helperText="Drag and drop a community background image here, or click to select"
                        />
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="flex flex-col gap-1.5">
                                <TextField
                                    label="Background Image Bucket Name"
                                    type="text"
                                    value={
                                        formData.backgroundImage?.bucketName ??
                                        ''
                                    }
                                    onChange={handleFieldChange(
                                        'backgroundImageBucketName'
                                    )}
                                    disabled
                                />
                                <Hint>Auto-filled when image is uploaded</Hint>
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <TextField
                                    label="Background Image Object Key"
                                    type="text"
                                    value={
                                        formData.backgroundImage?.objectKey ??
                                        ''
                                    }
                                    onChange={handleFieldChange(
                                        'backgroundImageObjectKey'
                                    )}
                                    disabled
                                />
                                <Hint>Auto-filled when image is uploaded</Hint>
                            </div>
                        </div>
                    </div>
                </FormSection>

                <FormSection title="Background Color Settings">
                    <ColorPicker
                        value={formData.color}
                        onChange={handleColorChange}
                        label="Background Color"
                        helperText="Select a background color for the community (hex format)"
                    />
                </FormSection>

                <FormSection
                    title="Description (Multi-language)"
                    description="Describe your community in multiple languages"
                >
                    <LocalizedTextArea
                        label="Community Description"
                        value={formData.description}
                        onChange={handleDescriptionChange}
                    />
                </FormSection>

                <FormSection title="Join Consents">
                    {formData.tenantMemberAutoJoinEnabled && (
                        <Alert variant="warning">
                            <AlertTitle>
                                Consents are currently disabled
                            </AlertTitle>
                            <AlertDescription>
                                Tenant auto-join is enabled, so consents cannot
                                be configured. Disable auto-join to set up
                                consents.
                            </AlertDescription>
                        </Alert>
                    )}

                    {!formData.tenantMemberAutoJoinEnabled && (
                        <div className="flex flex-col gap-4">
                            {consents.map((consent, index) => (
                                <div
                                    key={index}
                                    className="flex flex-col gap-4 rounded-lg border p-4"
                                >
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium">
                                            Consent #{index + 1}
                                        </p>
                                        <Button
                                            variant="ghost"
                                            size="icon-sm"
                                            className="text-destructive"
                                            aria-label="Remove consent"
                                            onClick={() => removeConsent(index)}
                                        >
                                            <Trash2 />
                                        </Button>
                                    </div>
                                    <LocalizedTextArea
                                        label="Consent text"
                                        value={consent.text}
                                        onChange={(value) =>
                                            updateConsentText(index, value)
                                        }
                                        error={
                                            consentErrors[index] || undefined
                                        }
                                        required
                                    />
                                    <div className="flex flex-wrap gap-6">
                                        <SwitchField
                                            label="Needs checkbox"
                                            checked={consent.needsCheckbox}
                                            onChange={(checked) =>
                                                updateConsentToggle(
                                                    index,
                                                    'needsCheckbox',
                                                    checked
                                                )
                                            }
                                        />
                                        {consent.needsCheckbox && (
                                            <SwitchField
                                                label="Required"
                                                checked={consent.required}
                                                onChange={(checked) =>
                                                    updateConsentToggle(
                                                        index,
                                                        'required',
                                                        checked
                                                    )
                                                }
                                            />
                                        )}
                                        <SwitchField
                                            label="Show after accept"
                                            checked={consent.showAfterAccept}
                                            disabled={!consent.required}
                                            onChange={(checked) =>
                                                updateConsentToggle(
                                                    index,
                                                    'showAfterAccept',
                                                    checked
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                            ))}

                            <Button
                                variant="outline"
                                className="w-full border-2 border-dashed py-6"
                                onClick={addConsent}
                            >
                                <Plus />
                                Add Consent
                            </Button>

                            {consents.length === 0 && (
                                <div className="rounded-lg border-2 border-dashed p-6 text-center text-muted-foreground">
                                    <p className="text-sm">
                                        No consents defined.
                                    </p>
                                    <p className="text-sm">
                                        Click "Add Consent" to define consents
                                        that members must accept when joining
                                        this community.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </FormSection>
            </form>

            <StickyBar>
                <Button
                    variant="ghost"
                    onClick={handleCancel}
                    disabled={saving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    loading={saving}
                    disabled={!isFormValid}
                >
                    <Save />
                    Save
                </Button>
            </StickyBar>
        </>
    );
});

export default EditCommunityForm;
